/**
 * ScenarioParser.ts - Parse and validate scenario JSON files for DEFCON TERMINAL
 *
 * Validates scenario structure, node references, and checks for dead ends.
 */

import type {
  Scenario,
  ScenarioNode,
  ChoiceNode,
  MultiSelectNode,
  TimedNode,
  NarrativeNode,
  ConsequenceNode,
  EndingNode,
  NodeType,
  PathType,
  CategoryType,
  OutcomeType,
} from '@shared/types';

/**
 * Validation error with context
 */
export interface ValidationError {
  code: string;
  message: string;
  path?: string;
  nodeId?: string;
  details?: Record<string, unknown>;
}

/**
 * Validation warning (non-fatal issues)
 */
export interface ValidationWarning {
  code: string;
  message: string;
  path?: string;
  nodeId?: string;
}

/**
 * Parse result
 */
export interface ParseResult {
  success: boolean;
  scenario?: Scenario;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

/**
 * Valid node types
 */
const VALID_NODE_TYPES: NodeType[] = [
  'narrative',
  'choice',
  'multi-select',
  'timed',
  'consequence',
  'ending',
];

/**
 * Valid path types
 */
const VALID_PATHS: PathType[] = [
  'sarn-west',
  'sarn-east',
  'phantom-directive',
  'red-shadow',
];

/**
 * Valid category types
 */
const VALID_CATEGORIES: CategoryType[] = [
  // Nuclear/Strategic categories
  'crisis-escalation',
  'first-strike',
  'accidental-war',
  'limited-exchange',
  'full-scale',
  'defensive-posture',
  'offensive-planning',
  'retaliatory',
  'retaliation',
  'counter-strike',
  'escalation',
  'accident',
  // Crisis categories
  'crisis',
  'internal-crisis',
  'diplomatic',
  'diplomatic-failure',
  'proxy-war',
  // Intelligence categories
  'intelligence',
  'endgame',
  'destabilization',
  'intelligence-extraction',
  'direct-action',
  'counter-intelligence',
  'special-recon',
  'active-measures',
  'illegal-networks',
  'tech-acquisition',
  'elimination',
  'counter-ops',
];

/**
 * Valid outcome types
 */
const VALID_OUTCOMES: OutcomeType[] = [
  'PEACEFUL_RESOLUTION',
  'LIMITED_CONVENTIONAL',
  'PROXY_CONFLICT',
  'TACTICAL_NUCLEAR',
  'LIMITED_EXCHANGE',
  'MAJOR_EXCHANGE',
  'FULL_THERMONUCLEAR',
  'MISSION_SUCCESS',
  'PARTIAL_SUCCESS',
  'COMPROMISED',
  'DIPLOMATIC_INCIDENT',
  'ESCALATION_TO_CONFLICT',
  'BLOWN_OPERATION',
];

/**
 * Valid difficulty levels
 */
const VALID_DIFFICULTIES = ['easy', 'medium', 'hard', 'extreme'] as const;

/**
 * ScenarioParser class - Validates and parses scenario JSON
 */
export class ScenarioParser {
  private errors: ValidationError[] = [];
  private warnings: ValidationWarning[] = [];

  /**
   * Parse and validate a scenario JSON object
   *
   * @param data - Raw JSON data to parse
   * @returns Parse result with scenario or errors
   */
  parse(data: unknown): ParseResult {
    this.errors = [];
    this.warnings = [];

    // Basic type check
    if (!data || typeof data !== 'object') {
      this.addError('INVALID_TYPE', 'Scenario must be an object');
      return { success: false, errors: this.errors, warnings: this.warnings };
    }

    const raw = data as Record<string, unknown>;

    // Validate required fields
    this.validateRequiredFields(raw);

    // If critical errors, return early
    if (this.errors.length > 0) {
      return { success: false, errors: this.errors, warnings: this.warnings };
    }

    // Validate individual fields
    this.validateId(raw.id);
    this.validateTitle(raw.title);
    this.validatePath(raw.path);
    this.validateCategory(raw.category);
    this.validateDescription(raw.description);
    this.validateDifficulty(raw.difficulty);
    this.validateMinimumDecisions(raw.minimumDecisions);
    this.validateInitialState(raw.initialState);
    this.validateNodes(raw.nodes);
    this.validateStartNode(raw.startNode, raw.nodes);

    // If still no errors, validate node connections
    if (this.errors.length === 0) {
      this.validateNodeConnections(
        raw.nodes as Record<string, ScenarioNode>,
        raw.startNode as string
      );
    }

    // Validate metadata if present
    if (raw.metadata) {
      this.validateMetadata(raw.metadata);
    }

    // Validate easter eggs if present
    if (raw.easterEggs) {
      this.validateEasterEggs(raw.easterEggs, raw.nodes as Record<string, ScenarioNode>);
    }

    // Return result
    if (this.errors.length > 0) {
      return { success: false, errors: this.errors, warnings: this.warnings };
    }

    return {
      success: true,
      scenario: raw as unknown as Scenario,
      errors: [],
      warnings: this.warnings,
    };
  }

  /**
   * Add a validation error
   */
  private addError(
    code: string,
    message: string,
    options?: { path?: string; nodeId?: string; details?: Record<string, unknown> }
  ): void {
    this.errors.push({
      code,
      message,
      ...options,
    });
  }

  /**
   * Add a validation warning
   */
  private addWarning(
    code: string,
    message: string,
    options?: { path?: string; nodeId?: string }
  ): void {
    this.warnings.push({
      code,
      message,
      ...options,
    });
  }

  /**
   * Validate required fields are present
   */
  private validateRequiredFields(data: Record<string, unknown>): void {
    const required = [
      'id',
      'title',
      'path',
      'category',
      'description',
      'difficulty',
      'minimumDecisions',
      'initialState',
      'nodes',
      'startNode',
    ];

    for (const field of required) {
      if (data[field] === undefined) {
        this.addError('MISSING_FIELD', `Missing required field: ${field}`, {
          path: field,
        });
      }
    }
  }

  /**
   * Validate scenario ID
   */
  private validateId(id: unknown): void {
    if (typeof id !== 'string' || id.length === 0) {
      this.addError('INVALID_ID', 'Scenario ID must be a non-empty string', {
        path: 'id',
      });
      return;
    }

    // Check ID format (lowercase, alphanumeric with dashes)
    if (!/^[a-z0-9-]+$/.test(id)) {
      this.addWarning(
        'ID_FORMAT',
        'Scenario ID should be lowercase alphanumeric with dashes',
        { path: 'id' }
      );
    }
  }

  /**
   * Validate scenario title
   */
  private validateTitle(title: unknown): void {
    if (typeof title !== 'string' || title.length === 0) {
      this.addError('INVALID_TITLE', 'Scenario title must be a non-empty string', {
        path: 'title',
      });
    }
  }

  /**
   * Validate path type
   */
  private validatePath(path: unknown): void {
    if (!VALID_PATHS.includes(path as PathType)) {
      this.addError(
        'INVALID_PATH',
        `Invalid path type. Must be one of: ${VALID_PATHS.join(', ')}`,
        { path: 'path', details: { provided: path } }
      );
    }
  }

  /**
   * Validate category type
   */
  private validateCategory(category: unknown): void {
    if (!VALID_CATEGORIES.includes(category as CategoryType)) {
      this.addError(
        'INVALID_CATEGORY',
        `Invalid category type. Must be one of: ${VALID_CATEGORIES.join(', ')}`,
        { path: 'category', details: { provided: category } }
      );
    }
  }

  /**
   * Validate description
   */
  private validateDescription(description: unknown): void {
    if (typeof description !== 'string' || description.length === 0) {
      this.addError(
        'INVALID_DESCRIPTION',
        'Scenario description must be a non-empty string',
        { path: 'description' }
      );
    }
  }

  /**
   * Validate difficulty
   */
  private validateDifficulty(difficulty: unknown): void {
    if (!VALID_DIFFICULTIES.includes(difficulty as typeof VALID_DIFFICULTIES[number])) {
      this.addError(
        'INVALID_DIFFICULTY',
        `Invalid difficulty. Must be one of: ${VALID_DIFFICULTIES.join(', ')}`,
        { path: 'difficulty', details: { provided: difficulty } }
      );
    }
  }

  /**
   * Validate minimum decisions
   */
  private validateMinimumDecisions(value: unknown): void {
    if (typeof value !== 'number' || value < 1 || !Number.isInteger(value)) {
      this.addError(
        'INVALID_MINIMUM_DECISIONS',
        'minimumDecisions must be a positive integer',
        { path: 'minimumDecisions', details: { provided: value } }
      );
    }
  }

  /**
   * Validate initial state
   */
  private validateInitialState(state: unknown): void {
    if (!state || typeof state !== 'object') {
      this.addError('INVALID_INITIAL_STATE', 'initialState must be an object', {
        path: 'initialState',
      });
      return;
    }

    const s = state as Record<string, unknown>;

    // Validate defcon
    if (typeof s.defcon !== 'number' || s.defcon < 1 || s.defcon > 5) {
      this.addError(
        'INVALID_DEFCON',
        'defcon must be a number between 1 and 5',
        { path: 'initialState.defcon', details: { provided: s.defcon } }
      );
    }

    // Validate worldTension
    if (
      typeof s.worldTension !== 'number' ||
      s.worldTension < 0 ||
      s.worldTension > 100
    ) {
      this.addError(
        'INVALID_WORLD_TENSION',
        'worldTension must be a number between 0 and 100',
        { path: 'initialState.worldTension', details: { provided: s.worldTension } }
      );
    }

    // Validate alliedMorale
    if (
      typeof s.alliedMorale !== 'number' ||
      s.alliedMorale < 0 ||
      s.alliedMorale > 100
    ) {
      this.addError(
        'INVALID_ALLIED_MORALE',
        'alliedMorale must be a number between 0 and 100',
        { path: 'initialState.alliedMorale', details: { provided: s.alliedMorale } }
      );
    }

    // Validate sovietAggression
    if (
      typeof s.sovietAggression !== 'number' ||
      s.sovietAggression < 0 ||
      s.sovietAggression > 100
    ) {
      this.addError(
        'INVALID_SOVIET_AGGRESSION',
        'sovietAggression must be a number between 0 and 100',
        {
          path: 'initialState.sovietAggression',
          details: { provided: s.sovietAggression },
        }
      );
    }

    // Validate date format
    if (typeof s.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s.date)) {
      this.addError('INVALID_DATE', 'date must be in YYYY-MM-DD format', {
        path: 'initialState.date',
        details: { provided: s.date },
      });
    }

    // Validate time format
    if (typeof s.time !== 'string' || !/^\d{2}:\d{2}:\d{2}Z?$/.test(s.time)) {
      this.addError('INVALID_TIME', 'time must be in HH:MM:SSZ format', {
        path: 'initialState.time',
        details: { provided: s.time },
      });
    }
  }

  /**
   * Validate nodes object
   */
  private validateNodes(nodes: unknown): void {
    if (!nodes || typeof nodes !== 'object' || Array.isArray(nodes)) {
      this.addError('INVALID_NODES', 'nodes must be an object', { path: 'nodes' });
      return;
    }

    const nodeMap = nodes as Record<string, unknown>;
    const nodeIds = Object.keys(nodeMap);

    if (nodeIds.length === 0) {
      this.addError('EMPTY_NODES', 'nodes object must contain at least one node', {
        path: 'nodes',
      });
      return;
    }

    // Validate each node
    for (const [nodeId, node] of Object.entries(nodeMap)) {
      this.validateNode(nodeId, node);
    }
  }

  /**
   * Validate a single node
   */
  private validateNode(nodeId: string, node: unknown): void {
    if (!node || typeof node !== 'object') {
      this.addError('INVALID_NODE', `Node "${nodeId}" must be an object`, {
        nodeId,
      });
      return;
    }

    const n = node as Record<string, unknown>;

    // Validate type
    if (!VALID_NODE_TYPES.includes(n.type as NodeType)) {
      this.addError(
        'INVALID_NODE_TYPE',
        `Node "${nodeId}" has invalid type. Must be one of: ${VALID_NODE_TYPES.join(', ')}`,
        { nodeId, details: { provided: n.type } }
      );
      return;
    }

    // Validate based on type
    switch (n.type) {
      case 'narrative':
        this.validateNarrativeNode(nodeId, n);
        break;
      case 'choice':
        this.validateChoiceNode(nodeId, n);
        break;
      case 'multi-select':
        this.validateMultiSelectNode(nodeId, n);
        break;
      case 'timed':
        this.validateTimedNode(nodeId, n);
        break;
      case 'consequence':
        this.validateConsequenceNode(nodeId, n);
        break;
      case 'ending':
        this.validateEndingNode(nodeId, n);
        break;
    }
  }

  /**
   * Validate narrative node
   */
  private validateNarrativeNode(nodeId: string, node: Record<string, unknown>): void {
    // Must have content array
    if (!Array.isArray(node.content) || node.content.length === 0) {
      this.addError(
        'INVALID_CONTENT',
        `Narrative node "${nodeId}" must have a non-empty content array`,
        { nodeId }
      );
    }

    // Must have next
    if (typeof node.next !== 'string' || node.next.length === 0) {
      this.addError(
        'MISSING_NEXT',
        `Narrative node "${nodeId}" must have a next node reference`,
        { nodeId }
      );
    }
  }

  /**
   * Validate choice node
   */
  private validateChoiceNode(nodeId: string, node: Record<string, unknown>): void {
    // Must have prompt
    if (typeof node.prompt !== 'string' || node.prompt.length === 0) {
      this.addError(
        'MISSING_PROMPT',
        `Choice node "${nodeId}" must have a prompt`,
        { nodeId }
      );
    }

    // Must have options array
    if (!Array.isArray(node.options) || node.options.length === 0) {
      this.addError(
        'MISSING_OPTIONS',
        `Choice node "${nodeId}" must have a non-empty options array`,
        { nodeId }
      );
      return;
    }

    // Validate each option
    this.validateOptions(nodeId, node.options);
  }

  /**
   * Validate multi-select node
   */
  private validateMultiSelectNode(nodeId: string, node: Record<string, unknown>): void {
    // Must have prompt
    if (typeof node.prompt !== 'string' || node.prompt.length === 0) {
      this.addError(
        'MISSING_PROMPT',
        `Multi-select node "${nodeId}" must have a prompt`,
        { nodeId }
      );
    }

    // Must have options array
    if (!Array.isArray(node.options) || node.options.length === 0) {
      this.addError(
        'MISSING_OPTIONS',
        `Multi-select node "${nodeId}" must have a non-empty options array`,
        { nodeId }
      );
      return;
    }

    // Validate selection bounds
    const minSelections = node.minSelections as number | undefined;
    const maxSelections = node.maxSelections as number | undefined;

    if (minSelections !== undefined && (typeof minSelections !== 'number' || minSelections < 1)) {
      this.addError(
        'INVALID_MIN_SELECTIONS',
        `Multi-select node "${nodeId}" has invalid minSelections`,
        { nodeId, details: { provided: minSelections } }
      );
    }

    if (maxSelections !== undefined && (typeof maxSelections !== 'number' || maxSelections < 1)) {
      this.addError(
        'INVALID_MAX_SELECTIONS',
        `Multi-select node "${nodeId}" has invalid maxSelections`,
        { nodeId, details: { provided: maxSelections } }
      );
    }

    if (
      minSelections !== undefined &&
      maxSelections !== undefined &&
      minSelections > maxSelections
    ) {
      this.addError(
        'INVALID_SELECTION_RANGE',
        `Multi-select node "${nodeId}" has minSelections > maxSelections`,
        { nodeId, details: { minSelections, maxSelections } }
      );
    }

    // Validate each option
    this.validateOptions(nodeId, node.options);
  }

  /**
   * Validate timed node
   */
  private validateTimedNode(nodeId: string, node: Record<string, unknown>): void {
    // Must have prompt
    if (typeof node.prompt !== 'string' || node.prompt.length === 0) {
      this.addError('MISSING_PROMPT', `Timed node "${nodeId}" must have a prompt`, {
        nodeId,
      });
    }

    // Must have options array
    if (!Array.isArray(node.options) || node.options.length === 0) {
      this.addError(
        'MISSING_OPTIONS',
        `Timed node "${nodeId}" must have a non-empty options array`,
        { nodeId }
      );
      return;
    }

    // Must have timeLimit
    if (typeof node.timeLimit !== 'number' || node.timeLimit <= 0) {
      this.addError(
        'MISSING_TIME_LIMIT',
        `Timed node "${nodeId}" must have a positive timeLimit`,
        { nodeId, details: { provided: node.timeLimit } }
      );
    }

    // Must have defaultOption
    if (typeof node.defaultOption !== 'string' || node.defaultOption.length === 0) {
      this.addError(
        'MISSING_DEFAULT_OPTION',
        `Timed node "${nodeId}" must have a defaultOption`,
        { nodeId }
      );
    } else {
      // Check defaultOption exists in options
      const options = node.options as Array<{ id: string }>;
      const defaultExists = options.some((opt) => opt.id === node.defaultOption);
      if (!defaultExists) {
        this.addError(
          'INVALID_DEFAULT_OPTION',
          `Timed node "${nodeId}" defaultOption "${node.defaultOption}" not found in options`,
          { nodeId, details: { defaultOption: node.defaultOption } }
        );
      }
    }

    // Validate each option
    this.validateOptions(nodeId, node.options);
  }

  /**
   * Validate consequence node
   */
  private validateConsequenceNode(nodeId: string, node: Record<string, unknown>): void {
    // Must have content array
    if (!Array.isArray(node.content) || node.content.length === 0) {
      this.addError(
        'INVALID_CONTENT',
        `Consequence node "${nodeId}" must have a non-empty content array`,
        { nodeId }
      );
    }

    // Must have next
    if (typeof node.next !== 'string' || node.next.length === 0) {
      this.addError(
        'MISSING_NEXT',
        `Consequence node "${nodeId}" must have a next node reference`,
        { nodeId }
      );
    }
  }

  /**
   * Validate ending node
   */
  private validateEndingNode(nodeId: string, node: Record<string, unknown>): void {
    // Must have outcomeType
    if (!VALID_OUTCOMES.includes(node.outcomeType as OutcomeType)) {
      this.addError(
        'INVALID_OUTCOME_TYPE',
        `Ending node "${nodeId}" has invalid outcomeType`,
        { nodeId, details: { provided: node.outcomeType } }
      );
    }

    // Must have content array
    if (!Array.isArray(node.content) || node.content.length === 0) {
      this.addError(
        'INVALID_CONTENT',
        `Ending node "${nodeId}" must have a non-empty content array`,
        { nodeId }
      );
    }

    // Must have score
    if (!node.score || typeof node.score !== 'object') {
      this.addError('MISSING_SCORE', `Ending node "${nodeId}" must have a score object`, {
        nodeId,
      });
    } else {
      const score = node.score as Record<string, unknown>;
      if (typeof score.base !== 'number') {
        this.addError(
          'INVALID_SCORE_BASE',
          `Ending node "${nodeId}" score must have a numeric base`,
          { nodeId }
        );
      }
    }
  }

  /**
   * Validate options array
   */
  private validateOptions(nodeId: string, options: unknown[]): void {
    const optionIds = new Set<string>();

    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (!option || typeof option !== 'object') {
        this.addError(
          'INVALID_OPTION',
          `Node "${nodeId}" option ${i} must be an object`,
          { nodeId }
        );
        continue;
      }

      const opt = option as Record<string, unknown>;

      // Must have id
      if (typeof opt.id !== 'string' || opt.id.length === 0) {
        this.addError(
          'MISSING_OPTION_ID',
          `Node "${nodeId}" option ${i} must have an id`,
          { nodeId }
        );
      } else {
        // Check for duplicate IDs
        if (optionIds.has(opt.id)) {
          this.addError(
            'DUPLICATE_OPTION_ID',
            `Node "${nodeId}" has duplicate option id "${opt.id}"`,
            { nodeId, details: { optionId: opt.id } }
          );
        }
        optionIds.add(opt.id);
      }

      // Must have text
      if (typeof opt.text !== 'string' || opt.text.length === 0) {
        this.addError(
          'MISSING_OPTION_TEXT',
          `Node "${nodeId}" option "${opt.id || i}" must have text`,
          { nodeId }
        );
      }

      // Must have next
      if (typeof opt.next !== 'string' || opt.next.length === 0) {
        this.addError(
          'MISSING_OPTION_NEXT',
          `Node "${nodeId}" option "${opt.id || i}" must have a next node reference`,
          { nodeId }
        );
      }
    }
  }

  /**
   * Validate start node exists
   */
  private validateStartNode(startNode: unknown, nodes: unknown): void {
    if (typeof startNode !== 'string') {
      return; // Already reported in required fields
    }

    if (nodes && typeof nodes === 'object') {
      const nodeMap = nodes as Record<string, unknown>;
      if (!nodeMap[startNode]) {
        this.addError(
          'INVALID_START_NODE',
          `Start node "${startNode}" does not exist in nodes`,
          { path: 'startNode', details: { startNode } }
        );
      }
    }
  }

  /**
   * Validate all node connections (detect dead ends and orphans)
   */
  private validateNodeConnections(
    nodes: Record<string, ScenarioNode>,
    startNode: string
  ): void {
    const referencedNodes = new Set<string>();
    const reachableNodes = new Set<string>();

    // Collect all referenced nodes and check references exist
    for (const [nodeId, node] of Object.entries(nodes)) {
      if (node.type === 'narrative' || node.type === 'consequence') {
        const next = (node as NarrativeNode | ConsequenceNode).next;
        referencedNodes.add(next);
        if (!nodes[next]) {
          this.addError(
            'INVALID_NODE_REFERENCE',
            `Node "${nodeId}" references non-existent node "${next}"`,
            { nodeId, details: { referencedNode: next } }
          );
        }
      }

      if (node.type === 'choice' || node.type === 'multi-select' || node.type === 'timed') {
        const choiceNode = node as ChoiceNode | MultiSelectNode | TimedNode;
        for (const option of choiceNode.options) {
          referencedNodes.add(option.next);
          if (!nodes[option.next]) {
            this.addError(
              'INVALID_OPTION_REFERENCE',
              `Node "${nodeId}" option "${option.id}" references non-existent node "${option.next}"`,
              { nodeId, details: { optionId: option.id, referencedNode: option.next } }
            );
          }
        }
      }
    }

    // Find reachable nodes via BFS from start
    const queue = [startNode];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (reachableNodes.has(current)) {
        continue;
      }
      reachableNodes.add(current);

      const node = nodes[current];
      if (!node) {
        continue;
      }

      if (node.type === 'narrative' || node.type === 'consequence') {
        const next = (node as NarrativeNode | ConsequenceNode).next;
        if (!reachableNodes.has(next)) {
          queue.push(next);
        }
      }

      if (node.type === 'choice' || node.type === 'multi-select' || node.type === 'timed') {
        const choiceNode = node as ChoiceNode | MultiSelectNode | TimedNode;
        for (const option of choiceNode.options) {
          if (!reachableNodes.has(option.next)) {
            queue.push(option.next);
          }
        }
      }
    }

    // Check for orphaned nodes
    for (const nodeId of Object.keys(nodes)) {
      if (nodeId !== startNode && !reachableNodes.has(nodeId)) {
        this.addWarning(
          'ORPHANED_NODE',
          `Node "${nodeId}" is not reachable from the start node`,
          { nodeId }
        );
      }
    }

    // Check for dead ends (non-ending nodes with no outgoing connections)
    for (const [nodeId, node] of Object.entries(nodes)) {
      if (node.type === 'ending') {
        continue; // Endings are intentional dead ends
      }

      if (node.type === 'narrative' || node.type === 'consequence') {
        // These have mandatory 'next' - already validated
        continue;
      }

      if (node.type === 'choice' || node.type === 'multi-select' || node.type === 'timed') {
        const choiceNode = node as ChoiceNode | MultiSelectNode | TimedNode;
        if (choiceNode.options.length === 0) {
          this.addError(
            'DEAD_END',
            `Node "${nodeId}" is a dead end with no options`,
            { nodeId }
          );
        }
      }
    }

    // Verify at least one ending is reachable
    const endings = Object.entries(nodes).filter(([, n]) => n.type === 'ending');
    const reachableEndings = endings.filter(([id]) => reachableNodes.has(id));

    if (reachableEndings.length === 0 && endings.length > 0) {
      this.addError(
        'NO_REACHABLE_ENDINGS',
        'No endings are reachable from the start node'
      );
    }
  }

  /**
   * Validate metadata object
   */
  private validateMetadata(metadata: unknown): void {
    if (typeof metadata !== 'object' || metadata === null) {
      this.addWarning('INVALID_METADATA', 'metadata should be an object', {
        path: 'metadata',
      });
      return;
    }

    const m = metadata as Record<string, unknown>;

    // Validate version format if present
    if (m.version !== undefined && typeof m.version !== 'string') {
      this.addWarning('INVALID_METADATA_VERSION', 'metadata.version should be a string', {
        path: 'metadata.version',
      });
    }

    // Validate lastUpdated format if present
    if (
      m.lastUpdated !== undefined &&
      (typeof m.lastUpdated !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(m.lastUpdated))
    ) {
      this.addWarning(
        'INVALID_METADATA_DATE',
        'metadata.lastUpdated should be in YYYY-MM-DD format',
        { path: 'metadata.lastUpdated' }
      );
    }
  }

  /**
   * Validate easter eggs array
   */
  private validateEasterEggs(
    easterEggs: unknown,
    nodes: Record<string, ScenarioNode>
  ): void {
    if (!Array.isArray(easterEggs)) {
      this.addWarning('INVALID_EASTER_EGGS', 'easterEggs should be an array', {
        path: 'easterEggs',
      });
      return;
    }

    for (let i = 0; i < easterEggs.length; i++) {
      const egg = easterEggs[i];
      if (!egg || typeof egg !== 'object') {
        this.addWarning('INVALID_EASTER_EGG', `easterEggs[${i}] should be an object`, {
          path: `easterEggs[${i}]`,
        });
        continue;
      }

      const e = egg as Record<string, unknown>;

      // Validate id
      if (typeof e.id !== 'string' || e.id.length === 0) {
        this.addWarning(
          'MISSING_EASTER_EGG_ID',
          `easterEggs[${i}] should have an id`,
          { path: `easterEggs[${i}].id` }
        );
      }

      // Validate triggerNode exists
      if (typeof e.triggerNode === 'string' && !nodes[e.triggerNode]) {
        this.addWarning(
          'INVALID_EASTER_EGG_NODE',
          `easterEggs[${i}] triggerNode "${e.triggerNode}" does not exist`,
          { path: `easterEggs[${i}].triggerNode` }
        );
      }

      // Validate points
      if (typeof e.points !== 'number') {
        this.addWarning(
          'MISSING_EASTER_EGG_POINTS',
          `easterEggs[${i}] should have numeric points`,
          { path: `easterEggs[${i}].points` }
        );
      }
    }
  }

  /**
   * Quick validation check (returns boolean only)
   */
  isValid(data: unknown): boolean {
    return this.parse(data).success;
  }
}

/**
 * Parse a scenario JSON string
 */
export function parseScenarioJSON(json: string): ParseResult {
  try {
    const data = JSON.parse(json);
    const parser = new ScenarioParser();
    return parser.parse(data);
  } catch (error) {
    return {
      success: false,
      errors: [
        {
          code: 'JSON_PARSE_ERROR',
          message: `Failed to parse JSON: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
      warnings: [],
    };
  }
}

/**
 * Parse a scenario object
 */
export function parseScenario(data: unknown): ParseResult {
  const parser = new ScenarioParser();
  return parser.parse(data);
}
